/**
 * AuditLog — reverse-chronological per-field edit trail for a venue (port of
 * the design's NBAuditLog, nb-token styled). Consumes the `audit` array the
 * venue read returns; each entry's jsonb `fieldChanges` renders as a
 * before→after diff.
 */
import { History } from 'lucide-react';

import type { VenueAuditEntry } from '../../../../api/venues';

import { AuditFieldRow } from './AuditFieldRow';

function isChanges(v: unknown): v is Record<string, { before: unknown; after: unknown }> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function AuditLog({ entries }: { entries: VenueAuditEntry[] }) {
  if (entries.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          fontSize: 12,
          fontStyle: 'italic',
          color: 'var(--nb-subtle)',
        }}
      >
        <History size={14} /> No edits recorded yet.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {entries.map((e, i) => {
        const changes = isChanges(e.fieldChanges) ? e.fieldChanges : {};
        const fields = Object.entries(changes);
        return (
          <div
            key={e.id}
            style={{
              padding: '8px 0',
              borderBottom: i < entries.length - 1 ? '1px solid var(--nb-border)' : 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
              <span
                style={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: 'var(--nb-ink)',
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                }}
              >
                {e.actorEmail || e.actorUserId}
              </span>
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 10, color: 'var(--nb-subtle)' }}>
                {new Date(e.changedAt).toLocaleString()}
              </span>
            </div>
            {fields.map(([field, change]) => (
              <AuditFieldRow key={field} field={field} change={change} />
            ))}
          </div>
        );
      })}
    </div>
  );
}
